<template>
  <div
    @click="$emit('selectUser', user)"
    :class="{focused: !disableFocus && selected,
     'human-item_bordered': bordered,
     'human-item_allow-focus': !disableFocus}"
    class="human-item">
    <div
      class="human-container">
      <router-link
        class="human-item__profile-image"
        tag="div"
        :to="`/people/${user.uid}`"
      >
        <profile-image
          :src="user.avatar_url"
          :user="user"
          :class="{ familyOwner: showFamilyOwnerBorder }"
        />
        <img
          v-if="showEventOwnerBadge"
          src="../../assets/img/event-owner-badge.svg"
          class="event-owner-badge" alt=""
        />
      </router-link>
      <div class="human-item__info">
        <component
          :is="addLinkToUsername ? 'router-link' : 'div'"
          :to="addLinkToUsername ? `/people/${user.uid}` : ''"
          class="human-item__name">
          <div
            @click="addLinkToUsername ? $emit('linkClick') : null"
          >
            {{ $getUserName(user) }}
          </div>
        </component>
        <div v-if="!showMoneyInput" class="human-item__status">
          <strong
            v-if="message === 'show all' && (user.balance || user.amount)"
            class="human-item__message"
          >{{ getPayoutMessage(user.balance, user) || getPayoutMessage(user.amount, user) }}
          </strong>
          <money-message
            v-if="(message === 'show all' || message === 'show money')
              && (!Array.isArray(user.balance))"
            :type="user.balance || user.amount | getPayoutType"
            :currency="currency || user.currency"
            :money-amount="moneyAmount || user.balance || user.amount || $t('CP.noDebts')"
          />
          <ul
            class="money-messages-list"
            v-else-if="(message === 'show all' || message === 'show money')
              && (Array.isArray(user.balance) && user.balance.length > 0)"
          >
            <li
              class="money-messages-item"
              v-for="(balance, index) of user.balance"
              :key="`${balance.balance}${index}`"
            >
              <money-message
                :type="balance.balance | getPayoutType"
                :currency="balance.currency"
                :money-amount="balance.balance || $t('CP.noDebts')"
              />
            </li>
          </ul>
          <money-message
            v-else-if="(message === 'show all' || message === 'show money')
              && (Array.isArray(user.balance) && user.balance.length === 0)"
            :type="0 | getPayoutType"
            :money-amount="$t('CP.noDebts')"
          />
        </div>
        <div class="human-item__status">
          <div v-if="showUserPhone" class="human-item__phone">
            {{ user.phone | formatPhone }}
          </div>
        </div>
      </div>
    </div>
    <div class="aside-wrapper">
      <div v-if="evenForRestTotal" class="human-item__money-display">
        <money-message
          :type="0 | getPayoutType"
          :money-amount="evenForRestTotal"
        />
      </div>
      <div v-else-if="showMoneyInput" class="human-item__money-form">
        <app-input-small
          :default-text="defaultMoney().toString()"
          class="human-item__transaction-money"
          v-model="money"
          @input="$emit('changeMoney', {money: money || 0, phone: user.phone})"
          to-fixed-two
          placeholder="0.00"
          type="number"
          :label="$t('CP.amount')"
        />
      </div>
        <delete-human-button
          v-if="showDeleteButton"
          :disabled="disableDeleteButton"
          :disabled-title="user.balance !== 0 ?
            $t('eventOptions.eventUnableToDeleteUserWithDebt') :
            $t('eventOptions.eventUnableToDeleteActiveUser')"
          @deleteHuman="deleteHuman(user)"
          class="delete-human-button"
        />
        <font-awesome-icon
          v-if="showDragIcon"
          icon="align-justify"
          class="align-justify-icon dragSwitch"
        />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ProfileImage from '../ProfileImage/ProfileImage';
import MoneyMessage from '../MoneyMessagge/MoneyMessage';
import DeleteHumanButton from '../DeleteHumanButton/DeleteHumanButton';
import AppInputSmall from '../AppInputSmall/AppInputSmall';

export default {
  name: 'HumanItem',
  components: {
    AppInputSmall,
    DeleteHumanButton,
    MoneyMessage,
    ProfileImage,
  },
  data() {
    return {
      money: '',
    };
  },
  mounted() {
    this.money = this.defaultMoney() || '';
    if (!this.money) {
      const { phone } = this.user;
      this.$emit('changeMoney', { money: 0, phone });
    }
  },
  computed: {
    ...mapState({
      me: (state) => state.user.user,
    }),
    is_touch_device() {
      return !!('touchstart' in window);
    },
  },
  methods: {
    async deleteHuman(user) {
      const { phone } = user;
      this.$emit('changeMoney', { money: 0, phone });
      this.$emit('deleteUser', user);
    },
    defaultMoney() {
      const splitMoney = this.$store.state.operations.purchaseInfo?.splitUsersMoney;
      if (splitMoney) {
        const defaultMoney = splitMoney
          .find((splitUser) => splitUser.phone === this.user.phone);
        if (defaultMoney && defaultMoney.money) {
          return defaultMoney.money;
        }
        return '';
      }
      return '';
    },
    getPayoutMessage(balance, user) {
      let message = '';
      if (balance > 0) {
        message = `${this.$t(`CP.theyOwesMe_${user.gender || '_M'}`)} `;
      }
      if (balance < 0) {
        message = `${this.$t(`CP.iOweThem_${this.me.gender || '_M'}`)} `;
      }
      return message;
    },
  },
  props: {
    bordered: {
      type: Boolean,
      default: true,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    showEventOwnerBadge: {
      type: Boolean,
      default: false,
    },
    showFamilyOwnerBorder: {
      type: Boolean,
      default: false,
    },
    moneyAmount: {
      type: Number,
      default: 0,
    },
    message: {
      type: String,
      default: 'dont show',
      validate: (val) => ['dont show', 'show money', 'show all'].includes(val),
    },
    currency: {
      type: String,
      default: '',
    },
    showMoneyInput: {
      type: Boolean,
      default: false,
    },
    inputMoney: {
      default: '',
    },
    showUserPhone: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    disableDeleteButton: {
      type: Boolean,
      default: false,
    },
    showDragIcon: {
      type: Boolean,
      default: false,
    },
    disableFocus: {
      type: Boolean,
      default: false,
    },
    addLinkToUsername: {
      type: Boolean,
      default: false,
    },
    evenForRestTotal: {
      type: Number,
      default: null,
    },
  },
};
</script>

<style lang="scss" scoped>
  .aside-wrapper {
    font-size: 0.8em;
    display: flex;
    align-items: center;
    height: 24px;
    justify-content: center;
    margin: auto;
    max-width: none;
  }
  .human-item {
    display: flex;
    box-sizing: border-box;
    padding: 9px 0;
    font-size: 1rem;
    color: var(--text-main-color);
    outline: none;
  }

  .human-container {
    display: flex;
    flex: 1;
  }

  .human-item_allow-focus {
    cursor: pointer;
  }

  .human-item_bordered {
    border-bottom: 1px solid #E3E9F5;
  }

  .human-item__profile-image {
    cursor: pointer;
    align-self: center;
    position: relative;
    border-radius: 50%;
  }
  .human-item__money-display {
    height: 100%;
    display: flex;
    align-items: center;
  }
  .familyOwner {
    border: 3px solid #f1c200;
    margin-left: -3px;
  }

  .focused .human-item__profile-image {
    box-shadow: 0 1px 6px rgba(41, 121, 255, 0.61)
  }

  .event-owner-badge {
    content: url("../../assets/img/event-owner-badge.svg");
    width: 18px;
    height: 18px;
    position: absolute;
    top: -4px;
    right: -4px;
  }

  .human-item__name {
    display: flex;
    color: var(--text-main-color);
    text-decoration: none;
  }

  .focused .human-item__name {
    color: var(--btn-primary);
  }

  .human-item__info {
    text-align: left;
    margin: 0 10px 0 10px;
    align-self: flex-start;
    flex: 1;
  }

  .human-item__status {
    display: flex;
    align-items: center;
    text-align: left;
    font-size: 0.8em;
  }

  .human-item__message {
    margin: 0 4px 0 0;
    opacity: 0.6;
  }

  .human-item__phone {
    margin-right: 8px;
    flex: 1 0 60%;
  }

  .human-item__money-form {
    margin-top: -16px;
    justify-self: flex-end;
  }
  .human-item__money {
    justify-content: left;
  }

  .money-messages-list {
    padding: 0;
    list-style-type: none;
  }

  .delete-human-button {
    margin: auto 0 auto 8px;
    height: 100%;
    display: flex;
    align-items: center;
  }
  .align-justify-icon {
    align-self: center;
  }
  .dragSwitch {
    cursor: pointer;
    padding: 2px;
    width: 18px;
    height: 20px;
  }

@media (max-width: $breakpoint-sm) {
  .aside-wrapper {
    width: 35%;
    max-width: 300px;
  }
}
</style>
